import { message, Modal, Space } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';

import {
  Button,
  DatePicker,
  SearchInput,
  Select,
} from '../../../../components/index';
import { useChangePagination } from '../../../../hooks';
import {
  DockingWasteCollectionItemType,
  DockingWasteCollectionParamsType,
  DockingWasteCollectionStatusType,
  useFillPendingDockingsWasteCollectionMutation,
  useGetDockingsWasteCollectionQuery,
  useLazyGetDockingsWasteCollectionQuery,
  useUpdateDockingsWasteCollectionMutation,
} from '../../../../services/wasteCollectionApi';
import { createDateString } from '../../../../utils/utils';
import { SectionHeader } from '../styles';
import { EnvironmentDrawer } from './environmentDrawer/EnvironmentDrawer';
import { EnvironmentTable } from './environmentTable/EnvironmentTable';

const { confirm } = Modal;

const wasteCollectionStatusOptions = [
  { label: 'Pendente', value: 'PENDING' },
  { label: 'Sim', value: 'YES' },
  { label: 'Não', value: 'NO' },
];

function countDockings(amount: number) {
  return amount === 1 ? `${amount} atracação` : `${amount} atracações`;
}

function formatReportDate(date: string) {
  return moment(date).format('DD/MM/YYYY');
}

export function EnvironmentSection() {
  const { onChangePagination, queryPage, setQueryPage } = useChangePagination();

  const [filters, setFilters] = useState<DockingWasteCollectionParamsType>({
    page: queryPage,
  });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: queryPage }));
  }, [queryPage]);

  const { data, isFetching } = useGetDockingsWasteCollectionQuery(filters);

  const [isVisibleDrawer, setIsVisibleDrawer] = useState(false);
  const [openedDocking, setOpenedDocking] =
    useState<DockingWasteCollectionItemType | null>(null);

  const [selectedDockingIds, setSelectedDockingIds] = useState<number[]>([]);

  const [updateDockingsWasteCollection] =
    useUpdateDockingsWasteCollectionMutation();
  const [fillPendingDockingsWasteCollection] =
    useFillPendingDockingsWasteCollectionMutation();
  const [countPendingDockings, { isFetching: isCountingPending }] =
    useLazyGetDockingsWasteCollectionQuery();

  const hasPeriod =
    !!filters.initial_date_report && !!filters.final_date_report;

  const selectedDocking =
    data?.results?.find(
      (docking) => docking.docking_id === openedDocking?.docking_id
    ) ?? openedDocking;

  function onOpenDrawer(docking: DockingWasteCollectionItemType) {
    setOpenedDocking(docking);
    setIsVisibleDrawer(true);
  }

  function onCloseDrawer() {
    setIsVisibleDrawer(false);
    setOpenedDocking(null);
  }

  function applyFilters(
    update: (
      prev: DockingWasteCollectionParamsType
    ) => DockingWasteCollectionParamsType
  ) {
    setQueryPage(1);
    setSelectedDockingIds([]);
    setFilters((prev) => ({ ...update(prev), page: 1 }));
  }

  function onSearch(value: string) {
    applyFilters((prev) => ({ ...prev, code_or_vessel__name: value }));
  }

  function onWasteCollectionSaved(dockingId: number) {
    setSelectedDockingIds((prev) => prev.filter((id) => id !== dockingId));
  }

  function onChangeStatus(status?: DockingWasteCollectionStatusType) {
    applyFilters((prev) => ({ ...prev, waste_collection_status: status }));
  }

  function onChangeDatePicker(dates: any) {
    if (dates === null) {
      applyFilters((prev) => ({
        ...prev,
        initial_date_report: undefined,
        final_date_report: undefined,
      }));
      return;
    }
    let filteredDates = {};
    if (dates && dates.length > 0 && dates[0]) {
      filteredDates = {
        ...filteredDates,
        initial_date_report: createDateString(dates[0], true),
      };
    }
    if (dates && dates.length > 1 && dates[1]) {
      filteredDates = {
        ...filteredDates,
        final_date_report: createDateString(dates[1], true),
      };
    }
    applyFilters((prev) => ({ ...prev, ...filteredDates }));
  }

  function onMarkSelected(hasWasteCollection: boolean) {
    const answer = hasWasteCollection ? 'Sim' : 'Não';
    confirm({
      title: `Marcar ${countDockings(
        selectedDockingIds.length
      )} como "${answer}"?`,
      content:
        'O valor substitui o que já estiver informado nas atracações selecionadas.',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      async onOk() {
        const response = await updateDockingsWasteCollection({
          docking_ids: selectedDockingIds,
          has_waste_collection: hasWasteCollection,
        });
        if ('data' in response) {
          setSelectedDockingIds([]);
          message.success(
            `Coleta de resíduo informada como "${answer}" em ${countDockings(
              response.data.updated
            )}.`
          );
        }
      },
    });
  }

  async function onMarkPendingAsNo() {
    const { initial_date_report, final_date_report, code_or_vessel__name } =
      filters;
    if (!initial_date_report || !final_date_report) {
      return;
    }

    const { data: pending, isError } = await countPendingDockings({
      initial_date_report,
      final_date_report,
      code_or_vessel__name,
      waste_collection_status: 'PENDING',
      page_size: 1,
    });
    if (isError || !pending) {
      return;
    }
    if (pending.count === 0) {
      message.info('Não há atracações pendentes no período.');
      return;
    }

    const searchDescription = code_or_vessel__name
      ? ` que correspondem a "${code_or_vessel__name}"`
      : '';
    confirm({
      title: `Marcar ${countDockings(pending.count)} pendentes como "Não"?`,
      content: `Atracações desatracadas entre ${formatReportDate(
        initial_date_report
      )} e ${formatReportDate(
        final_date_report
      )}${searchDescription} ainda sem a coleta informada. As que já estão informadas não mudam.`,
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      async onOk() {
        const response = await fillPendingDockingsWasteCollection({
          has_waste_collection: false,
          initial_date_report,
          final_date_report,
          code_or_vessel__name,
        });
        if ('data' in response) {
          setSelectedDockingIds([]);
          message.success(
            `Coleta de resíduo informada como "Não" em ${countDockings(
              response.data.updated
            )}.`
          );
        }
      },
    });
  }

  return (
    <>
      <SectionHeader style={{ flexDirection: 'column', gap: '15px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              marginInlineEnd: '10px',
            }}
          >
            <SearchInput
              size="large"
              allowClear
              placeholder="Pesquisar embarcação/atracação"
              style={{ width: '400px' }}
              onChange={debounce((evt) => onSearch(evt.target.value), 500)}
            />
            <div>
              <strong>{selectedDockingIds.length} </strong>
              {selectedDockingIds.length === 1 ? (
                <span>atracação selecionada</span>
              ) : (
                <span>atracações selecionadas</span>
              )}
            </div>
          </div>
          <Space size={18}>
            <Button
              onClick={() => onMarkSelected(true)}
              disabled={selectedDockingIds.length === 0}
            >
              Marcar como Sim
            </Button>
            <Button
              onClick={() => onMarkSelected(false)}
              disabled={selectedDockingIds.length === 0}
            >
              Marcar como Não
            </Button>
            <Button
              onClick={onMarkPendingAsNo}
              disabled={!hasPeriod}
              loading={isCountingPending}
            >
              Marcar pendentes do período como Não
            </Button>
          </Space>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
          }}
        >
          <span>Filtro por período de desatracação:</span>
          <DatePicker
            allowClear
            onChange={onChangeDatePicker}
            minuteStep={1}
            isRangePicker
            showTime={false}
            format={['DD/MM/YYYY']}
            style={{ width: '240px' }}
          />
          <span>Coleta de resíduo:</span>
          <Select
            allowClear
            placeholder="Todas"
            options={wasteCollectionStatusOptions}
            value={filters.waste_collection_status}
            onChange={(value) =>
              onChangeStatus(value as DockingWasteCollectionStatusType)
            }
            style={{ width: '160px' }}
          />
        </div>
      </SectionHeader>
      <EnvironmentTable
        dataSource={data?.results || []}
        onChangePagination={onChangePagination}
        total={data?.count}
        isLoading={isFetching}
        queryPage={queryPage}
        showSizeChanger={false}
        itemsPerPage={data?.results?.length}
        onOpenDrawer={onOpenDrawer}
        selectedDockingIds={selectedDockingIds}
        onChangeSelection={setSelectedDockingIds}
      />
      <EnvironmentDrawer
        isVisible={isVisibleDrawer}
        onClose={onCloseDrawer}
        selectedDocking={selectedDocking}
        onWasteCollectionSaved={onWasteCollectionSaved}
      />
    </>
  );
}
